import React, { useCallback, useMemo, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Paper,
  Tabs,
  Tab,
  Box,
  Grid,
  TextField,
  TextEditor,
  Typography,
  useMediaQuery,
} from "..";
import VoicePreview from "../../pages/Campaigns/Single/VoicePreview";
import TTYPreview from "../../pages/Campaigns/Single/TTYPreview";
import _ from "lodash";

const useStyles = makeStyles((theme) => ({
  previewRoot: {
    flexWrap: "nowrap",
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
    },
  },
  tabs: {
    width: 120,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    background: theme.palette.background.default,
  },
  tab: {
    minWidth: 120,
  },
  previewTitle: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  },
  previewPanel: {
    minWidth: 0,
    maxWidth: "100%",
  },
  tabContent: {
    minWidth: 0,
    maxWidth: "100%",
  },
}));

const TabPanel = memo(({ children, value, index, ...rest }) => (
  <div {...rest} role="tabpanel" hidden={value !== index}>
    {value === index && children}
  </div>
));

function CampaignPreview({ masterTemplate = {}, ...rest }) {
  const classes = useStyles();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const [currTab, setCurrTab] = useState(0);
  const {
    emailTemplate,
    smsTemplate,
    voiceTemplate,
    ttyTemplate,
  } = masterTemplate;

  const onTabChange = useCallback((ev, tab) => setCurrTab(tab), [setCurrTab]);
  const readOnlyProps = useMemo(() => ({ readOnly: true }), []);
  let tabCounter = 0;

  return (
    <Paper {...rest}>
      <Grid container className={classes.previewRoot} alignItems="stretch">
        <Grid item className={classes.tabs}>
          <Tabs
            orientation={isNarrow ? "horizontal" : "vertical"}
            indicatorColor="primary"
            textColor="primary"
            value={currTab}
            onChange={onTabChange}
          >
            {Boolean(emailTemplate) && (
              <Tab
                label={t("Email")}
                className={classes.tab}
                data-test-id="approverspage-preview-email-tab"
              />
            )}
            {Boolean(smsTemplate) && (
              <Tab
                label={t("SMS")}
                className={classes.tab}
                data-test-id="approverspage-preview-sms-tab"
              />
            )}
            {/* {Boolean(voiceTemplate?.live) && ( */}
            {Boolean(voiceTemplate?.freeswitch?.dialPlan?.person) && (
              <Tab
                label={t("Voice")}
                className={classes.tab}
                data-test-id="approverspage-preview-voice-live-tab"
              />
            )}
            {/* {Boolean(voiceTemplate?.answeringMachine) && ( */}
            {Boolean(voiceTemplate?.freeswitch?.dialPlan?.machine) && (
              <Tab
                label={t("Answering Machine")}
                className={classes.tab}
                data-test-id="approverspage-preview-voice-machine-tab"
              />
            )}
            {Boolean(ttyTemplate?.freeswitch?.dialPlan?.person) && (
              <Tab
                label={t("TTY")}
                className={classes.tab}
                data-test-id="approverspage-preview-tty-tab"
              />
            )}
          </Tabs>
        </Grid>
        <Grid item xs className={classes.previewPanel}>
          <Box p={2} className={classes.tabContent}>
            {Boolean(emailTemplate) && (
              <TabPanel value={currTab} index={tabCounter++}>
                <TextEditor
                  data-test-id="approverspage-preview-email"
                  value={emailTemplate.template}
                  readOnly
                />
              </TabPanel>
            )}
            {Boolean(smsTemplate) && (
              <TabPanel value={currTab} index={tabCounter++}>
                <TextField
                  data-test-id="approverspage-preview-sms"
                  multiline
                  rows={3}
                  rowsMax={5}
                  variant="outlined"
                  fullWidth
                  value={smsTemplate.template}
                  InputProps={readOnlyProps}
                />
              </TabPanel>
            )}
            {/* {Boolean(voiceTemplate?.live) && ( */}
            {Boolean(voiceTemplate?.freeswitch?.dialPlan?.person) && (
              <TabPanel value={currTab} index={tabCounter++}>
                <VoicePreview
                  // voiceArray={voiceTemplate.live}
                  voiceArray={
                    voiceTemplate?.freeswitch?.dialPlan?.person?.steps
                  }
                  data-test-id="approverspage-preview-voice-live"
                />
              </TabPanel>
            )}
            {/* {Boolean(voiceTemplate?.answeringMachine) && ( */}
            {Boolean(voiceTemplate?.freeswitch?.dialPlan?.machine) && (
              <TabPanel value={currTab} index={tabCounter++}>
                <VoicePreview
                  // voiceArray={voiceTemplate.answeringMachine}
                  voiceArray={
                    voiceTemplate?.freeswitch?.dialPlan?.machine?.steps
                  }
                  data-test-id="approverspage-preview-voice-machine"
                />
              </TabPanel>
            )}
            {Boolean(ttyTemplate?.freeswitch?.dialPlan?.person) && (
              <TabPanel value={currTab} index={tabCounter++}>
                <Typography>
                  Teletype is a message format for people who are deaf, hard of
                  hearing, or speech-impaired.
                </Typography>
                <Typography style={{ color: theme.palette.secondary.main }}>
                  {(() => {
                    const path =
                      ttyTemplate?.freeswitch?.dialPlan?.person?.steps[0].path;
                    return _.last(path.split("/"));
                  })()}
                </Typography>
                <TTYPreview
                  tty={
                    ttyTemplate?.freeswitch?.dialPlan?.person?.steps[0]?.path
                  }
                  data-test-id="campaign-preview-tty"
                />
              </TabPanel>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default memo(CampaignPreview);
